import { AnalyticsBrowser } from '@segment/analytics-next'

// Initialize Segment Analytics
export const analytics = AnalyticsBrowser.load({ 
  writeKey: '5ok9z6bHd2tBoz6jjarcJcA5NXbwTSon' 
})

// Sampling constants for different event types
const LARGE_EVENT_SAMPLING = 0.01 // Keep only 1% of high-volume events (skip 99%)

// Keeper utility function for analytics sampling
// Usage: if (!shouldKeep(LARGE_EVENT_SAMPLING)) return // Keep only 1% of large events
// Usage: if (!shouldKeep(0.1)) return  // Keep only 10% of events (skip 90%)
// Usage: if (!shouldKeep(0.5)) return  // Keep only 50% of events (skip 50%)
function shouldKeep(keepPercent: number): boolean {
  return Math.random() <= keepPercent
}

// Session tracking for idempotent analytics - one event per type per session
interface SessionTracker {
  sdkLoaded: boolean
  widgetLoaded: boolean
  widgetSeen: boolean
}

const sessionTracker: SessionTracker = {
  sdkLoaded: false,
  widgetLoaded: false,
  widgetSeen: false
}

// Helper function to get the parent domain
const getParentDomain = (): string => {
  try {
    // Try to access parent origin directly (may fail due to cross-origin restrictions)
    return window.parent.location.hostname
  } catch (error) {
    // Fallback to document.referrer if cross-origin access is blocked
    if (document.referrer) {
      try {
        return new URL(document.referrer).hostname
      } catch (urlError) {
        console.warn('Could not parse referrer URL:', document.referrer)
      }
    }
    
    // Final fallback to current origin if all else fails
    console.warn('Could not determine parent domain, using current domain as fallback')
    return window.location.hostname
  }
}

// Helper function to get conversation length (number of messages)
const getConversationLength = (messages: any[] = []): number => {
  return messages.length
}

// Generate thread ID for conversation tracking
export const generateThreadId = (): string => {
  return `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Track SDK loaded (first stage of funnel) - idempotent per session, sampled at 1%
export const trackSDKLoaded = (projectId: string, sdkType: 'simple' | 'shadow' = 'simple') => {
  if (sessionTracker.sdkLoaded) {
    return // Already tracked for this session
  }
  
  // Keep only 1% of large events (skip 99%)
  if (!shouldKeep(LARGE_EVENT_SAMPLING)) {
    sessionTracker.sdkLoaded = true
    return
  }
  
const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('SDK Loaded', {
    projectId,
    sdkType,
    domain: getParentDomain(),
    timestamp: Date.now(),
    ...positioningMetadata
  })
  
  sessionTracker.sdkLoaded = true
}

// Track widget loaded (second stage of funnel) - idempotent per session, sampled at 1%
export const trackWidgetLoaded = (projectId: string) => {
  if (sessionTracker.widgetLoaded) {
    return // Already tracked for this session
  }
  
  // Keep only 1% of large events (skip 99%)
  if (!shouldKeep(LARGE_EVENT_SAMPLING)) {
    sessionTracker.widgetLoaded = true
    return
  }
  
  const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('Widget Loaded', {
    projectId,
    type: 'widget',
    domain: getParentDomain(),
    timestamp: Date.now(),
    ...positioningMetadata
  })
  
  sessionTracker.widgetLoaded = true
}

// Initialize analytics with project ID (backwards compatibility)
export const initializeAnalytics = (projectId: string) => {
  trackWidgetLoaded(projectId)
}

// Atomic UI event tracking
export const trackInputFocused = (projectId: string, threadId: string, messages: any[] = []) => {
  const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('Input Focused', {
    projectId,
    threadId,
    domain: getParentDomain(),
    conversationLength: getConversationLength(messages),
    ...positioningMetadata
  })
}

export const trackInputBlurred = (projectId: string, threadId: string, messages: any[] = []) => {
  const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('Input Blurred', {
    projectId,
    threadId,
    domain: getParentDomain(),
    conversationLength: getConversationLength(messages),
    ...positioningMetadata
  })
}

export const trackUserTyping = (projectId: string, threadId: string, characterCount: number, messages: any[] = []) => {
  const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('User Started Typing', {
    projectId,
    threadId,
    domain: getParentDomain(),
    conversationLength: getConversationLength(messages),
    initialCharacterCount: characterCount,
    ...positioningMetadata
  })
}

export const trackSendButtonClicked = (projectId: string, threadId: string, messageContent: string, messages: any[] = [], options?: { think?: boolean; deepSearch?: boolean }) => {
  const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('Send Button Clicked', {
    projectId,
    threadId,
    domain: getParentDomain(),
    conversationLength: getConversationLength(messages),
    messageContent,
    messageLength: messageContent.length,
    hasThinkMode: options?.think || false,
    hasDeepSearch: options?.deepSearch || false,
    ...positioningMetadata
  })
}

export const trackSuggestionLoaded = (projectId: string, threadId: string, suggestionCount: number, messages: any[] = []) => {
  const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('Suggestions Loaded', {
    projectId,
    threadId,
    domain: getParentDomain(),
    conversationLength: getConversationLength(messages),
    suggestionCount,
    ...positioningMetadata
  })
}

export const trackSuggestionClicked = (projectId: string, threadId: string, suggestionText: string, messages: any[] = []) => {
  const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('Suggestion Clicked', {
    projectId,
    threadId,
    domain: getParentDomain(),
    conversationLength: getConversationLength(messages),
    suggestionText: suggestionText.substring(0, 100),
    suggestionLength: suggestionText.length,
    ...positioningMetadata
  })
}

// Track watermark clicked (when someone clicks "powered by whtvr.ai")
export const trackWatermarkClicked = (projectId: string, threadId: string, messages: any[] = []) => {
  const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('Watermark Clicked', {
    projectId,
    threadId,
    domain: getParentDomain(),
    conversationLength: getConversationLength(messages),
    timestamp: Date.now(),
    ...positioningMetadata
  })
}

// Track link clicked (when someone clicks on links in chat messages)
export const trackLinkClicked = (projectId: string, threadId: string, linkUrl: string, linkText: string, messages: any[] = []) => {
  const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('Link Clicked', {
    projectId,
    threadId,
    domain: getParentDomain(),
    conversationLength: getConversationLength(messages),
    linkUrl: linkUrl.substring(0, 200), // Limit URL length for analytics
    linkText: linkText.substring(0, 100), // Limit link text length
    linkLength: linkUrl.length,
    timestamp: Date.now(),
    ...positioningMetadata
  })
}


// Track widget seen (third stage of funnel) - idempotent per session
export const trackWidgetSeen = (projectId: string) => {
  if (sessionTracker.widgetSeen) {
    return // Already tracked for this session
  }
  
  const positioningMetadata = getWidgetPositioningMetadata()
  analytics.track('Widget Seen', {
    projectId,
    domain: getParentDomain(),
    timestamp: Date.now(),
    ...positioningMetadata
  })
  
  sessionTracker.widgetSeen = true
}


// Get widget positioning metadata for analytics
const getWidgetPositioningMetadata = (): { widgetPosition?: string } => {
  try {
    // Try to get positioning from Shadow DOM SDK instance
    if (typeof window !== 'undefined' && (window as any).kwrySDKInstance) {
      const position = (window as any).kwrySDKInstance.getWidgetPosition?.()
      if (position) {
        return { widgetPosition: position }
      }
    }
    
    // Fallback: try to get from simple embed instance
    if (typeof window !== 'undefined' && (window as any).kwryEmbedInstance) {
      // Simple embed doesn't have positioning info directly, but we can try to infer it
      // For now, we'll just note that it's from simple embed
      return { widgetPosition: 'unavaliable' }
    }
    
    return {}
  } catch (error) {
    // Silently fail - don't break analytics if positioning info isn't available
    return {}
  }
}



// Debug helper to check session tracking state
export const getSessionTrackingState = () => {
  return {
    sdkLoaded: sessionTracker.sdkLoaded,
    widgetLoaded: sessionTracker.widgetLoaded,
    widgetSeen: sessionTracker.widgetSeen
  }
}

// Debug helper to reset session tracking (for testing purposes)
export const resetSessionTracking = () => {
  sessionTracker.sdkLoaded = false
  sessionTracker.widgetLoaded = false
  sessionTracker.widgetSeen = false
}
 